import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { FileTokenService } from '../services/file-token.service';

/**
 * <img [tradxFile]="key"> or <a [tradxFile]="key">: directive that resolves
 * the S3 key via FileTokenService.urlFor() and sets the host's `src` (for
 * <img>) or `href` (for <a>) once the URL is ready.
 *
 * Replaces today's `<img [src]="S3URL + key">` / `<a [href]="S3URL + key">`
 * pattern so the (A)+(B) hybrid for the file proxy works uniformly across
 * static-binding HTML sites. TS-built URLs use FileTokenService.urlFor()
 * directly with `[src]="someUrl$ | async"`.
 *
 * The directive re-fetches whenever the input key changes (ngOnChanges) and
 * tears down any in-flight subscription on destroy.
 */
@Directive({
  selector: '[tradxFile]',
})
export class TradxFileDirective implements OnChanges, OnDestroy {
  // tslint:disable-next-line: no-input-rename
  @Input('tradxFile') key: string;

  // Optional fallback used when the key is falsy or the URL fails to resolve.
  // Common pattern: avatar that falls back to a local asset when the user
  // has no profile image. Replaces today's ternary `[src]="key ? S3URL+key
  // : '/assets/...'"`.
  // tslint:disable-next-line: no-input-rename
  @Input('tradxFileFallback') fallback: string;

  private sub: Subscription | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private fileTokens: FileTokenService,
  ) {}

  ngOnChanges(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.teardown();
  }

  private refresh(): void {
    this.teardown();

    const native = this.el.nativeElement;
    const attr = native.tagName === 'A' ? 'href' : 'src';

    if (!this.key) {
      this.applyFallbackOrClear(attr);
      return;
    }

    this.sub = this.fileTokens.urlFor(this.key).subscribe(url => {
      if (url) {
        native.setAttribute(attr, url);
      } else {
        this.applyFallbackOrClear(attr);
      }
    });
  }

  private applyFallbackOrClear(attr: string): void {
    const native = this.el.nativeElement;
    if (this.fallback) {
      native.setAttribute(attr, this.fallback);
    } else {
      native.removeAttribute(attr);
    }
  }

  private teardown(): void {
    if (this.sub) {
      this.sub.unsubscribe();
      this.sub = null;
    }
  }
}
